<!--
	Ported from analytics-design/src/components/SentimentDashboard.tsx, then rewired to real data -
	see the component's own comment for what changed. "Sentiment by Brand Role" is hidden
	(2026-10-06, product decision 2026-10-05: not in v1), see the @if (false) block below.

	SETTE redesign (CLIPPING-598): token-by-token restyle, "Reach" -> "Visibility" in all copy.
	Two Фаза 6 / Правило Г fixes carried over from the design doc: "Net Sentiment (Index) by
	Brand" had its own 4-tier threshold with a blue step at +20..+39 (Принцип 1 violation) -
	replaced with the shared symmetric sentimentScaleColor; the Topic x Brand heatmap moved from
	an ad-hoc 7-tier emerald/rose scale to the same 5-level sentiment scale used everywhere else
	sentiment is shown. Several `.startsWith('+')` sign checks on formatted text moved to real
	Tone fields, since the text now carries U+2212 for negatives.
-->
<div class="space-y-4">
	@if (showSkeleton) {
	<app-analytics-skeleton [kpiCount]="7" [chartCount]="2"></app-analytics-skeleton>
	} @else if (loadError) {
	<app-analytics-error-state [error]="errorState" (retry)="fetch()"></app-analytics-error-state>
	} @else if (isEmptyPeriod) {
	<app-analytics-empty-state variant="no-mentions"></app-analytics-empty-state>
	} @else {
	<div class="space-y-4" [class.opacity-50]="isRefreshing" [class.pointer-events-none]="isRefreshing">

	<!-- 1. TOP HEADER ROW -->
	<div class="bg-surface px-4 py-3 rounded-2xl border border-line flex flex-col xl:flex-row xl:items-center justify-between gap-3">
		<div class="flex flex-col lg:flex-row lg:items-center gap-4">
			<div>
				<div class="flex items-center gap-2">
					<h1 class="overline-label text-ink">{{ 'ANALYTICS_GLOSSARY.SENTIMENT' | translate }}</h1>
					<span class="px-2 py-0.5 rounded-full text-[11px] font-medium flex items-center gap-1" [class]="toneClasses(netSentimentIndexTone)">
						<mat-icon class="!w-3 !h-3 !text-xs">mood</mat-icon>
						<span>{{ 'ANALYTICS_SENTIMENT.NET_SENTIMENT_INDEX_BADGE' | translate }}: {{ netSentimentIndexLabel }}</span>
					</span>
				</div>
				<p class="text-ink-subtle text-xs mt-0.5">{{ 'ANALYTICS_SENTIMENT.SUBTITLE' | translate }}</p>
			</div>

			<div class="flex items-center gap-3 lg:border-l lg:border-line lg:pl-4 caption text-ink-subtle font-normal shrink-0">
				<span class="text-ink-subtle font-medium hidden sm:inline">{{ 'ANALYTICS_SENTIMENT.BRAND_ROLE_LABEL' | translate }}:</span>
				<div class="flex items-center gap-1.5">
					<span class="w-2.5 h-2.5 rounded-sm bg-blue shrink-0 inline-block"></span>
					<span class="font-medium text-ink">{{ 'ANALYTICS_BRANDS.OUR_BRAND_CLIENT' | translate }}</span>
				</div>
				<div class="flex items-center gap-1.5 ml-1">
					<span class="w-2.5 h-2.5 rounded-sm bg-ink-subtle shrink-0 inline-block"></span>
					<span class="font-medium text-ink-muted">{{ 'ANALYTICS_GLOSSARY.COMPETITORS' | translate }}</span>
				</div>
				<!-- Hidden 2026-10-06 (product decision 2026-10-05): not in v1 ("Manage Brand Roles"). Button + modal kept, not deleted. -->
				@if (false) {
				<button type="button" (click)="openManageModal()" class="text-blue font-medium flex items-center gap-1 hover:underline ml-2 cursor-pointer">
					<span>Manage Brands</span>
					<mat-icon class="!w-3 !h-3 !text-xs">open_in_new</mat-icon>
				</button>
				}
			</div>
		</div>
	</div>

	<!-- 2. KPI CARDS -->
	<div class="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
		<app-kpi-tile [label]="('ANALYTICS_GLOSSARY.POSITIVE' | translate) + ' %'" infoKey="ANALYTICS_INFO.SENTIMENT.POSITIVE_NEUTRAL_NEGATIVE_PCT" icon="mood" iconColorClass="text-positive" [value]="positiveKpi.value" [trendUp]="positiveKpi.trendUp" [deltaLabel]="positiveKpi.deltaLabel" [sampleSize]="summaryMentions"></app-kpi-tile>
		<app-kpi-tile [label]="('ANALYTICS_GLOSSARY.NEUTRAL' | translate) + ' %'" infoKey="ANALYTICS_INFO.SENTIMENT.POSITIVE_NEUTRAL_NEGATIVE_PCT" icon="sentiment_neutral" iconColorClass="text-ink-subtle" [value]="neutralKpi.value" [trendUp]="neutralKpi.trendUp" [deltaLabel]="neutralKpi.deltaLabel" [sampleSize]="summaryMentions"></app-kpi-tile>
		<app-kpi-tile [label]="('ANALYTICS_GLOSSARY.NEGATIVE' | translate) + ' %'" infoKey="ANALYTICS_INFO.SENTIMENT.POSITIVE_NEUTRAL_NEGATIVE_PCT" icon="mood_bad" iconColorClass="text-negative" [value]="negativeKpi.value" [trendUp]="negativeKpi.trendUp" [trendColorClass]="negativeKpi.trendColorClass" [deltaLabel]="negativeKpi.deltaLabel" [sampleSize]="summaryMentions"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_GLOSSARY.NET_SENTIMENT' | translate" infoKey="ANALYTICS_INFO.COMMON.NET_SENTIMENT" icon="speed" iconColorClass="text-ink-subtle" [value]="netSentimentKpi.value" [trendUp]="netSentimentKpi.trendUp" [deltaLabel]="netSentimentKpi.deltaLabel" [sampleSize]="summaryMentions"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_SENTIMENT.POSITIVE_MENTIONS_LABEL' | translate" infoKey="ANALYTICS_INFO.SENTIMENT.POSITIVE_NEGATIVE_NEUTRAL_MENTIONS" icon="thumb_up" iconColorClass="text-ink-subtle" [value]="fmtInt(positiveCount)" [deltaLabel]="'ANALYTICS_SENTIMENT.OF_TOTAL' | translate : { total: fmtInt(summaryMentions) }" [sampleSize]="summaryMentions"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_SENTIMENT.NEGATIVE_MENTIONS_LABEL' | translate" infoKey="ANALYTICS_INFO.SENTIMENT.POSITIVE_NEGATIVE_NEUTRAL_MENTIONS" icon="thumb_down" iconColorClass="text-ink-subtle" [value]="fmtInt(negativeCount)" [deltaLabel]="'ANALYTICS_SENTIMENT.OF_TOTAL' | translate : { total: fmtInt(summaryMentions) }" [sampleSize]="summaryMentions"></app-kpi-tile>
		<app-kpi-tile [label]="'ANALYTICS_SENTIMENT.NEUTRAL_MENTIONS_LABEL' | translate" infoKey="ANALYTICS_INFO.SENTIMENT.POSITIVE_NEGATIVE_NEUTRAL_MENTIONS" icon="remove_circle" iconColorClass="text-ink-subtle" [value]="fmtInt(neutralCount)" [deltaLabel]="'ANALYTICS_SENTIMENT.OF_TOTAL' | translate : { total: fmtInt(summaryMentions) }" [sampleSize]="summaryMentions"></app-kpi-tile>
	</div>

	<!-- 3. MAIN WIDGETS GRID -->
	<div class="space-y-4">
		<!-- ROW 1: Widgets 1, 2, 3 -->
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
			<!-- WIDGET 1: Sentiment Trend Over Time -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">1. {{ 'ANALYTICS_SENTIMENT.SENTIMENT_TREND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.SENTIMENT.SENTIMENT_TREND"></app-info-icon></h3>
					<div class="flex flex-wrap items-center gap-2.5 mb-1.5 caption font-medium">
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-positive"></span> {{ 'ANALYTICS_GLOSSARY.POSITIVE' | translate }} %</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-neutral-bar"></span> {{ 'ANALYTICS_GLOSSARY.NEUTRAL' | translate }} %</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-negative"></span> {{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }} %</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-0.5 bg-blue"></span> {{ 'ANALYTICS_SENTIMENT.NET_INDEX_LEGEND' | translate }}</span>
					</div>
				</div>
				<div class="relative w-full min-h-[250px] h-full flex-1">
					<app-chart type="line" [data]="sentimentTrendChartData" [options]="sentimentTrendChartOptions" height="100%"></app-chart>
				</div>
				@if (summaryMentions > 0) {
				<app-sample-size-note class="block mt-1" [count]="summaryMentions"></app-sample-size-note>
				}
			</div>

			<!-- WIDGET 2: Sentiment Distribution -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div class="flex items-center justify-between mb-1">
					<h3 class="section-title flex items-center gap-1">2. {{ 'ANALYTICS_SENTIMENT.SENTIMENT_DISTRIBUTION_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.SENTIMENT.SENTIMENT_DISTRIBUTION"></app-info-icon></h3>
					<span class="caption text-ink-subtle font-normal">{{ 'ANALYTICS_SENTIMENT.TOTAL_MENTIONS_BADGE' | translate : { count: fmtInt(summaryMentions) } }}</span>
				</div>

				<div class="grid grid-cols-12 gap-2 items-center flex-1">
					<div class="col-span-7 relative w-full min-h-[240px] h-full flex items-center justify-center">
						<app-chart type="doughnut" [data]="sentimentDistChartData" [options]="sentimentDistChartOptions" height="100%"></app-chart>
						<div class="absolute inset-0 flex flex-col items-center justify-center pointer-events-none text-center">
							<span class="text-xl font-medium text-ink leading-tight tabular-nums">{{ fmtInt(summaryMentions) }}</span>
							<span class="overline-label text-ink-subtle">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</span>
						</div>
					</div>

					<div class="col-span-5 space-y-2 text-[10px]">
						<div class="p-2 rounded-xl bg-positive-tint">
							<div class="flex items-center justify-between">
								<span class="font-medium text-positive flex items-center gap-1"><span class="w-2 h-2 rounded-[3px] bg-positive"></span> {{ 'ANALYTICS_GLOSSARY.POSITIVE' | translate }}</span>
								<span class="font-medium text-positive tabular-nums">{{ positivePct }}</span>
							</div>
							<span class="text-[10px] text-positive">{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: positiveCount } }}</span>
						</div>
						<div class="p-2 rounded-xl bg-neutral-tint">
							<div class="flex items-center justify-between">
								<span class="font-medium text-ink-subtle flex items-center gap-1"><span class="w-2 h-2 rounded-[3px] bg-neutral-bar"></span> {{ 'ANALYTICS_GLOSSARY.NEUTRAL' | translate }}</span>
								<span class="font-medium text-ink-subtle tabular-nums">{{ neutralPct }}</span>
							</div>
							<span class="text-[10px] text-ink-subtle">{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: neutralCount } }}</span>
						</div>
						<div class="p-2 rounded-xl bg-negative-tint">
							<div class="flex items-center justify-between">
								<span class="font-medium text-negative flex items-center gap-1"><span class="w-2 h-2 rounded-[3px] bg-negative"></span> {{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }}</span>
								<span class="font-medium text-negative tabular-nums">{{ negativePct }}</span>
							</div>
							<span class="text-[10px] text-negative">{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: negativeCount } }}</span>
						</div>
					</div>
				</div>
			</div>

			<!-- WIDGET 3: Sentiment by Brand -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[350px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">3. {{ 'ANALYTICS_SENTIMENT.SENTIMENT_BY_BRAND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.SENTIMENT.SENTIMENT_BY_BRAND"></app-info-icon></h3>
					<div class="flex items-center gap-3 mb-2 caption font-medium">
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-positive"></span> {{ 'ANALYTICS_GLOSSARY.POSITIVE' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-neutral-bar"></span> {{ 'ANALYTICS_GLOSSARY.NEUTRAL' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-negative"></span> {{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }}</span>
					</div>
				</div>

				<div class="space-y-3 flex-1 flex flex-col justify-center py-1">
					@for (b of brandSentimentData; track b.brand) {
					<div class="space-y-1">
						<div class="flex items-center justify-between text-[11px]">
							<div class="flex items-center gap-1.5">
								<span class="font-medium" [class]="b.client ? 'text-blue' : 'text-ink'">{{ b.brand }}</span>
								@if (b.client) {
								<span class="bg-blue text-on-blue text-[9px] px-1 py-0.2 rounded-full font-medium">{{ 'ANALYTICS_GLOSSARY.CLIENT' | translate }}</span>
								}
							</div>
							<span class="flex items-center gap-1 text-ink-subtle font-medium text-[10px]">
								{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: fmtInt(b.mentions) } }}
								@if (b.lowData) {
								<app-low-data-badge></app-low-data-badge>
								}
							</span>
						</div>

						@if (b.noData) {
						<div class="h-4 w-full bg-surface-gray rounded-full flex items-center justify-between px-2 text-[10px] text-ink-subtle">
							<span class="font-medium text-ink-muted">{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: fmtInt(b.mentions) } }}</span>
							<span class="text-ink-subtle italic">{{ 'ANALYTICS_SENTIMENT.TOO_FEW_MENTIONS' | translate }}</span>
						</div>
						} @else {
						<div class="h-4 w-full flex gap-[2px] text-[9px] font-medium text-on-blue" [class.opacity-50]="b.lowData">
							<div [style.width.%]="b.posPct" class="bg-positive rounded-full flex items-center justify-center" [title]="(('ANALYTICS_GLOSSARY.POSITIVE' | translate) + ': ' + b.posPct) + '%'">{{ b.posPct > 15 ? b.posPct + '%' : '' }}</div>
							<div [style.width.%]="b.neuPct" class="bg-neutral-bar rounded-full flex items-center justify-center text-ink-subtle" [title]="(('ANALYTICS_GLOSSARY.NEUTRAL' | translate) + ': ' + b.neuPct) + '%'">{{ b.neuPct > 15 ? b.neuPct + '%' : '' }}</div>
							<div [style.width.%]="b.negPct" class="bg-negative rounded-full flex items-center justify-center" [title]="(('ANALYTICS_GLOSSARY.NEGATIVE' | translate) + ': ' + b.negPct) + '%'">{{ b.negPct > 10 ? b.negPct + '%' : '' }}</div>
						</div>
						}
					</div>
					}
					@if (!loading && brandSentimentData.length === 0) {
					<div class="caption text-ink-subtle text-center py-2">{{ 'ANALYTICS_BRANDS.NO_BRAND_DATA' | translate }}</div>
					}
				</div>
			</div>
		</div>

		<!-- ROW 2: Widgets 4, 5, 6 -->
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
			<!-- WIDGET 4: Sentiment by Topic (Top 10) -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<h3 class="section-title mb-2 flex items-center gap-1">4. {{ 'ANALYTICS_SENTIMENT.SENTIMENT_BY_TOPIC_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.SENTIMENT.SENTIMENT_BY_TOPIC"></app-info-icon></h3>
					<div class="overflow-x-auto max-h-[265px]">
						<table class="w-full text-left text-xs">
							<thead>
								<tr class="border-b border-line overline-label text-ink-subtle sticky top-0 bg-surface">
									<th class="pb-2">{{ 'ANALYTICS_GLOSSARY.TOPIC' | translate }}</th>
									<th class="pb-2 text-right text-positive">{{ 'ANALYTICS_SENTIMENT.POS_PERCENT_HEADER' | translate }}</th>
									<th class="pb-2 text-right text-ink-subtle">{{ 'ANALYTICS_SENTIMENT.NEU_PERCENT_HEADER' | translate }}</th>
									<th class="pb-2 text-right text-negative">{{ 'ANALYTICS_TOPICS.NEG_PERCENT_HEADER' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_SENTIMENT.NET_SENT_HEADER' | translate }}</th>
								</tr>
							</thead>
							<tbody>
								@for (item of topTopicsSentiment; track item.topic) {
								<tr class="border-t border-line hover:bg-surface-blue transition-colors">
									<td class="py-1.5 font-medium text-ink truncate max-w-[130px]">
										<div class="truncate font-medium">{{ item.topic }}</div>
										<span class="flex items-center gap-1 text-[9px] text-ink-subtle">
											{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: fmtInt(item.mentions) } }}
											@if (item.lowData) {
											<app-low-data-badge></app-low-data-badge>
											}
										</span>
									</td>
									@if (item.noData) {
									<td colspan="4" class="py-1.5 text-right text-ink-subtle italic text-[10px]">{{ 'ANALYTICS_SENTIMENT.TOO_FEW_MENTIONS_SHORT' | translate }}</td>
									} @else {
									<td class="py-1.5 text-right font-medium text-positive tabular-nums" [class.opacity-50]="item.lowData">{{ item.pos }}</td>
									<td class="py-1.5 text-right text-ink-subtle font-medium tabular-nums" [class.opacity-50]="item.lowData">{{ item.neu }}</td>
									<td class="py-1.5 text-right font-medium text-negative tabular-nums" [class.opacity-50]="item.lowData">{{ item.neg }}</td>
									<td class="py-1.5 text-right" [class.opacity-50]="item.lowData">
										<span class="font-medium px-1 py-0.2 rounded-full text-[10px] tabular-nums" [class]="toneClasses(item.netTone)">{{ item.net }}</span>
									</td>
									}
								</tr>
								}
								@if (!loading && topTopicsSentiment.length === 0) {
								<tr><td colspan="5" class="py-4 text-center caption text-ink-subtle">{{ 'ANALYTICS_SENTIMENT.NO_TOPIC_DATA' | translate }}</td></tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<!-- WIDGET 5: Net Sentiment (Index) by Brand -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">5. {{ 'ANALYTICS_SENTIMENT.NET_SENTIMENT_BY_BRAND_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.SENTIMENT.NET_SENTIMENT_BY_BRAND"></app-info-icon></h3>
				</div>

				<div class="space-y-3 flex-1 flex flex-col justify-center py-1">
					@for (b of brandNetSentimentData; track b.brand) {
					<div class="space-y-1">
						<div class="flex items-center justify-between text-[11px]">
							<div class="flex items-center gap-1.5">
								<span class="font-medium" [class]="b.client ? 'text-blue' : 'text-ink'">{{ b.brand }}</span>
								@if (b.client) {
								<span class="bg-blue text-on-blue text-[9px] px-1 py-0.2 rounded-full font-medium">{{ 'ANALYTICS_GLOSSARY.CLIENT' | translate }}</span>
								}
							</div>
							<div class="flex items-center gap-2">
								<span class="text-ink-subtle text-[10px]">{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: fmtInt(b.mentions) } }}</span>
								@if (!b.lowData) {
								<span class="font-medium px-1.5 py-0.2 rounded-full text-[10px] tabular-nums" [class]="netScoreBadgeClass(b.netScore)">{{ b.netScoreStr }}</span>
								} @else {
								<span class="text-[10px] text-ink-subtle italic">{{ 'ANALYTICS_SENTIMENT.TOO_FEW_SHORT' | translate }}</span>
								}
							</div>
						</div>

						@if (b.lowData) {
						<div class="h-4 w-full bg-surface-gray rounded-full flex items-center justify-between px-2 text-[10px] text-ink-subtle">
							<span class="font-medium text-ink-muted">{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: fmtInt(b.mentions) } }}</span>
							<span class="text-ink-subtle italic">{{ 'ANALYTICS_SENTIMENT.TOO_FEW_MENTIONS' | translate }}</span>
						</div>
						} @else {
						<div class="h-4 w-full bg-neutral-tint rounded-full overflow-hidden flex items-center">
							<div [style.width.%]="barWidthPct(b.netScore)" class="h-full rounded-full flex items-center px-1.5 text-[9px] font-medium text-on-blue transition-all duration-300" [class]="netScoreBarClass(b.netScore)">{{ b.netScoreStr }} pts</div>
						</div>
						}
					</div>
					}
					@if (!loading && brandNetSentimentData.length === 0) {
					<div class="caption text-ink-subtle text-center py-2">{{ 'ANALYTICS_BRANDS.NO_BRAND_DATA' | translate }}</div>
					}
				</div>
			</div>

			<!-- WIDGET 6: Sentiment by Media Type -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<h3 class="section-title mb-1 flex items-center gap-1">6. {{ 'ANALYTICS_SENTIMENT.SENTIMENT_BY_MEDIA_TYPE_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.SENTIMENT.SENTIMENT_BY_MEDIA_TYPE"></app-info-icon></h3>
					<div class="flex items-center gap-3 mb-2 caption font-medium">
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-positive"></span> {{ 'ANALYTICS_GLOSSARY.POSITIVE' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-neutral-bar"></span> {{ 'ANALYTICS_GLOSSARY.NEUTRAL' | translate }}</span>
						<span class="flex items-center gap-1 text-ink-muted"><span class="w-2 h-2 rounded-[3px] bg-negative"></span> {{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }}</span>
					</div>
				</div>

				<div class="space-y-3 flex-1 flex flex-col justify-center py-1">
					@for (m of mediaSentimentData; track m.type) {
					<div class="space-y-1">
						<div class="flex items-center justify-between text-[11px]">
							<span class="font-medium text-ink">{{ m.type }}</span>
							<div class="flex items-center gap-2">
								<span class="flex items-center gap-1 text-ink-subtle text-[10px]">
									{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: fmtInt(m.mentions) } }}
									@if (m.lowData) {
									<app-low-data-badge></app-low-data-badge>
									}
								</span>
								@if (!m.noData) {
								<span class="font-medium px-1.5 py-0.2 rounded-full text-[10px] tabular-nums" [class]="toneClasses(m.netTone)">{{ m.net }}</span>
								} @else {
								<span class="text-[10px] text-ink-subtle italic">{{ 'ANALYTICS_SENTIMENT.TOO_FEW_SHORT' | translate }}</span>
								}
							</div>
						</div>

						@if (m.noData) {
						<div class="h-3.5 w-full bg-surface-gray rounded-full flex items-center justify-between px-2 text-[9px] text-ink-subtle">
							<span class="font-medium text-ink-muted">{{ 'ANALYTICS_TOPICS.SAMPLE_NOTE' | translate : { count: fmtInt(m.mentions) } }}</span>
							<span class="text-ink-subtle italic">{{ 'ANALYTICS_SENTIMENT.TOO_FEW_MENTIONS' | translate }}</span>
						</div>
						} @else {
						<div class="h-3.5 w-full flex gap-[2px] text-[9px] font-medium text-on-blue" [class.opacity-50]="m.lowData">
							<div [style.width.%]="m.posPct" class="bg-positive rounded-full flex items-center justify-center">{{ m.posPct > 15 ? m.posPct + '%' : '' }}</div>
							<div [style.width.%]="m.neuPct" class="bg-neutral-bar rounded-full flex items-center justify-center text-ink-subtle">{{ m.neuPct > 15 ? m.neuPct + '%' : '' }}</div>
							<div [style.width.%]="m.negPct" class="bg-negative rounded-full flex items-center justify-center">{{ m.negPct > 10 ? m.negPct + '%' : '' }}</div>
						</div>
						}
					</div>
					}
					@if (!loading && mediaSentimentData.length === 0) {
					<div class="caption text-ink-subtle text-center py-2">{{ 'ANALYTICS_COVERAGE_ANALYSIS.NO_MEDIA_TYPE_DATA' | translate }}</div>
					}
				</div>
			</div>
		</div>

		<!-- ROW 3: Widgets 7, 8, 9 -->
		<div class="grid grid-cols-1 lg:grid-cols-3 gap-4 items-stretch">
			<!-- WIDGET 7: Negative Sentiment Drivers -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<div class="flex items-center justify-between mb-2">
						<h3 class="section-title flex items-center gap-1">7. {{ 'ANALYTICS_SENTIMENT.NEGATIVE_DRIVERS_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.SENTIMENT.NEGATIVE_DRIVERS"></app-info-icon></h3>
						<span class="caption text-negative font-medium bg-negative-tint px-2 py-0.5 rounded-full">{{ 'ANALYTICS_SENTIMENT.TOP_ISSUES_BADGE' | translate }}</span>
					</div>
					<div class="overflow-x-auto">
						<table class="w-full text-left text-xs">
							<thead>
								<tr class="border-b border-line overline-label text-ink-subtle">
									<th class="pb-2">{{ 'ANALYTICS_GLOSSARY.TOPIC' | translate }}</th>
									<th class="pb-2 text-right text-negative">{{ 'ANALYTICS_GLOSSARY.NEGATIVE' | translate }}</th>
									<th class="pb-2 text-right">{{ 'ANALYTICS_GLOSSARY.MENTIONS' | translate }}</th>
								</tr>
							</thead>
							<tbody>
								@for (item of negativeDrivers; track item.issue) {
								<tr class="border-t border-line hover:bg-surface-blue transition-colors">
									<td class="py-2.5 font-medium text-ink truncate max-w-[150px]">{{ item.issue }}</td>
									<td class="py-2.5 text-right font-medium text-negative tabular-nums">{{ item.negCount }}</td>
									<td class="py-2.5 text-right font-medium text-ink tabular-nums">{{ item.mentions }}</td>
								</tr>
								}
								@if (!loading && negativeDrivers.length === 0) {
								<tr><td colspan="3" class="py-4 text-center caption text-ink-subtle">{{ (negativeCount > 0 ? 'ANALYTICS_SENTIMENT.NEGATIVE_DRIVERS_TOPICS_PENDING' : 'ANALYTICS_SENTIMENT.NO_NEGATIVE_DRIVERS') | translate }}</td></tr>
								}
							</tbody>
						</table>
					</div>
				</div>
			</div>

			<!-- WIDGET 8: Sentiment Heatmap (Topic x Brand) -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<h3 class="section-title mb-2 flex items-center gap-1">8. {{ 'ANALYTICS_SENTIMENT.SENTIMENT_HEATMAP_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.SENTIMENT.SENTIMENT_HEATMAP"></app-info-icon></h3>
					<div class="overflow-x-auto">
						<table class="w-full text-center text-xs">
							<thead>
								<tr class="border-b border-line overline-label text-ink-subtle">
									<th class="pb-2 text-left">{{ 'ANALYTICS_GLOSSARY.TOPIC' | translate }}</th>
									@for (col of heatmapColumns; track col.key) {
									<th class="pb-2">{{ col.label }}</th>
									}
								</tr>
							</thead>
							<tbody>
								@for (row of heatmapRows; track row.topic) {
								<tr class="border-t border-line hover:bg-surface-blue transition-colors">
									<td class="py-1.5 text-left font-medium text-ink truncate max-w-[100px] text-[10px]">{{ row.topic }}</td>
									@for (col of heatmapColumns; track col.key) {
									<td class="py-1 px-1"><span class="inline-block w-full py-0.5 px-0.5 rounded-lg text-[9px] tabular-nums" [class]="getHeatmapColor(row.cells[col.key].raw)">{{ row.cells[col.key].text }}</span></td>
									}
								</tr>
								}
								@if (!loading && heatmapRows.length === 0) {
								<tr><td [attr.colspan]="heatmapColumns.length + 1" class="py-4 caption text-ink-subtle">{{ 'ANALYTICS_TOPICS.NO_BRAND_TOPIC_DATA' | translate }}</td></tr>
								}
							</tbody>
						</table>

						<div class="mt-2.5 pt-2 border-t border-line flex items-center justify-between caption text-ink-subtle font-normal">
							<span class="font-medium text-negative">-100</span>
							<div class="flex-1 mx-3 flex gap-[2px] h-1.5">
								<span class="flex-1 rounded-full bg-negative"></span>
								<span class="flex-1 rounded-full bg-negative-soft"></span>
								<span class="flex-1 rounded-full bg-neutral-bar"></span>
								<span class="flex-1 rounded-full bg-positive-soft"></span>
								<span class="flex-1 rounded-full bg-positive"></span>
							</div>
							<span class="font-medium text-positive">+100</span>
						</div>
					</div>
				</div>
			</div>

			<!-- WIDGET 9: Recent Highly Negative Mentions -->
			<div class="bg-surface p-4 rounded-2xl border border-line flex flex-col justify-between min-h-[360px]">
				<div>
					<div class="flex items-center justify-between mb-2">
						<h3 class="section-title flex items-center gap-1">9. {{ 'ANALYTICS_SENTIMENT.RECENT_NEGATIVE_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.SENTIMENT.RECENT_NEGATIVE_MENTIONS"></app-info-icon></h3>
						<span class="caption text-negative font-medium bg-negative-tint px-2 py-0.5 rounded-full">{{ 'ANALYTICS_SENTIMENT.CRITICAL_ALERTS_BADGE' | translate }}</span>
					</div>
					<div class="space-y-2">
						@for (item of recentNegativeMentions; track item.id) {
						<div class="p-2.5 rounded-xl border border-line hover:bg-surface-blue transition-colors">
							<div class="flex items-center justify-between gap-2 mb-1">
								<div class="flex items-center gap-1.5">
									<span class="font-medium px-1.5 py-0.2 rounded-full text-[9px]" [class]="item.isClient ? 'bg-blue text-on-blue' : 'bg-surface-gray text-ink-muted'">{{ item.brand }}</span>
									<span class="text-ink-subtle text-[10px] font-medium">{{ item.media }} ({{ item.mediaType }})</span>
								</div>
								<span class="text-ink-subtle text-[9px]">{{ item.dateLabel }}</span>
							</div>
							<h4 class="text-xs font-medium text-ink line-clamp-1 leading-snug">{{ item.headline }}</h4>
							@if (item.brief) {
							<p class="caption text-ink-subtle font-normal line-clamp-1 mt-0.5">{{ item.brief }}</p>
							}
							<div class="flex items-center justify-between mt-1.5 caption text-ink-subtle font-normal">
								<span>{{ 'ANALYTICS_GLOSSARY.VISIBILITY' | translate }}: <strong class="text-ink-muted font-medium">{{ fmt1(item.reachScore) }}</strong> / 100</span>
								<span class="text-negative font-medium bg-negative-tint px-2 py-0.5 rounded-full tabular-nums">{{ 'ANALYTICS_GLOSSARY.IMPACT' | translate }}: {{ item.impactScore }}</span>
							</div>
						</div>
						}
						@if (!loading && recentNegativeMentions.length === 0) {
						<div class="caption text-ink-subtle text-center py-4">{{ 'ANALYTICS_SENTIMENT.NO_RECENT_NEGATIVE' | translate }}</div>
						}
					</div>
				</div>
			</div>
		</div>

		<!-- ROW 4: Widget 10 (full width) -->
		<div class="bg-surface p-4 rounded-2xl border border-line space-y-3">
			<div class="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-line pb-2">
				<div>
					<h3 class="section-title flex items-center gap-1">10. {{ 'ANALYTICS_SENTIMENT.SENTIMENT_SHIFTS_TITLE' | translate }}<app-info-icon key="ANALYTICS_INFO.SENTIMENT.SENTIMENT_SHIFTS"></app-info-icon></h3>
					<p class="caption text-ink-subtle font-normal mt-0.5">{{ 'ANALYTICS_SENTIMENT.SENTIMENT_SHIFTS_DESC' | translate }}</p>
				</div>
				<div class="flex items-center gap-3 caption font-medium">
					<span class="flex items-center gap-1 text-positive"><mat-icon class="!w-3.5 !h-3.5 !text-sm">trending_up</mat-icon> {{ 'ANALYTICS_SENTIMENT.IMPROVEMENT_LEGEND' | translate }}</span>
					<span class="flex items-center gap-1 text-negative"><mat-icon class="!w-3.5 !h-3.5 !text-sm">trending_down</mat-icon> {{ 'ANALYTICS_SENTIMENT.DETERIORATION_LEGEND' | translate }}</span>
				</div>
			</div>

			<div class="overflow-x-auto">
				<table class="w-full text-left text-xs">
					<thead>
						<tr class="border-b border-line overline-label text-ink-subtle bg-page">
							<th class="py-2 px-3">{{ 'ANALYTICS_SENTIMENT.ENTITY_HEADER' | translate }}</th>
							<th class="py-2 px-3">{{ 'ANALYTICS_BRANDS.COL_TYPE' | translate }}</th>
							<th class="py-2 px-3 text-right">{{ 'ANALYTICS_SENTIMENT.CHANGE_HEADER' | translate }}</th>
						</tr>
					</thead>
					<tbody>
						@for (item of sentimentShiftsData; track item.name) {
						<tr class="border-t border-line hover:bg-surface-blue transition-colors">
							<td class="py-2 px-3 font-medium text-ink text-xs">{{ item.name }}</td>
							<td class="py-2 px-3">
								<span class="text-[10px] font-medium px-2 py-0.5 rounded-full bg-surface-gray text-ink-muted">{{ item.type }}</span>
							</td>
							<td class="py-2 px-3 text-right font-medium">
								<span class="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-[11px] font-medium tabular-nums" [class]="item.improved ? 'bg-positive-tint text-positive' : 'bg-negative-tint text-negative'">
									<mat-icon class="!w-3 !h-3 !text-xs">{{ item.improved ? 'trending_up' : 'trending_down' }}</mat-icon>
									<span>{{ item.badge }}</span>
								</span>
								<div class="caption text-ink-subtle font-normal mt-0.5">{{ item.details }}</div>
							</td>
						</tr>
						}
						@if (!loading && sentimentShiftsData.length === 0) {
						<tr><td colspan="3" class="py-4 text-center caption text-ink-subtle">{{ sentimentShiftsEmptyMessage }}</td></tr>
						}
					</tbody>
				</table>
			</div>
		</div>
	</div>

	<!-- 4. FOOTER NOTE -->
	<div class="text-center py-2.5 px-4 bg-surface-gray rounded-2xl border border-line text-ink-subtle caption font-normal leading-relaxed">
		<span>{{ 'ANALYTICS_SENTIMENT.FOOTER_NOTE' | translate }}</span>
	</div>
	</div>
	}

	<!-- MANAGE BRANDS MODAL ("Sentiment by Brand Role") - hidden 2026-10-06 (product decision 2026-10-05): not in v1 -->
	@if (false && showManageModal) {
	<div class="fixed inset-0 bg-ink/40 backdrop-blur-xs z-50 flex items-center justify-center p-4">
		<div class="bg-surface rounded-2xl border border-line shadow-pop max-w-lg w-full p-5 space-y-4">
			<div class="flex items-center justify-between border-b border-line pb-3">
				<div class="flex items-center gap-2">
					<mat-icon class="!w-4 !h-4 !text-base text-blue">business</mat-icon>
					<h3 class="item-title text-ink">Sentiment by Brand Role</h3>
				</div>
				<button type="button" (click)="closeManageModal()" class="text-ink-subtle hover:text-ink p-1 rounded-lg hover:bg-surface-gray cursor-pointer">
					<mat-icon class="!w-4 !h-4 !text-base">close</mat-icon>
				</button>
			</div>

			<p class="text-ink-subtle text-xs">Brand role/keyword configuration lives in Settings, not here.</p>

			<div class="space-y-2 max-h-64 overflow-y-auto pr-1">
				@for (brand of brandNetSentimentData; track brand.brand) {
				<div class="flex items-center justify-between p-2.5 rounded-xl border border-line bg-surface-gray">
					<div class="font-medium text-ink">{{ brand.brand }}</div>
					<div class="text-right">
						<span class="px-2 py-0.5 rounded-full text-[10px] font-medium" [class]="brand.client ? 'bg-blue text-on-blue' : 'bg-surface-gray text-ink-muted'">{{ brand.client ? 'Our Brand' : 'Competition' }}</span>
						<div class="text-[10px] text-ink-subtle mt-0.5">{{ brand.netScoreStr }} pts ({{ fmtInt(brand.mentions) }} mentions)</div>
					</div>
				</div>
				}
			</div>

			<div class="flex items-center justify-between pt-3 border-t border-line">
				<button type="button" (click)="addCompetitorBrand()" class="flex items-center gap-1 text-blue font-medium text-xs hover:underline cursor-pointer">
					<mat-icon class="!w-3.5 !h-3.5 !text-sm">add</mat-icon>
					<span>Add Competitor Brand</span>
				</button>
				<button type="button" (click)="closeManageModal()" class="h-10 px-[18px] bg-blue hover:bg-blue-hover text-on-blue font-medium rounded-full text-xs transition-colors cursor-pointer">Done</button>
			</div>
		</div>
	</div>
	}
</div>
